import { type FC, useState, useCallback, type KeyboardEvent, type FocusEvent } from 'react';
import classnames from 'classnames';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import DiscordLogo from 'dibs-icons/exports/legacy/DiscordLogo.ts';

import styles from './DiscordTooltip.module.css';

const TOOLTIP_ID = 'discord-tooltip';
const TRIGGER_ID = 'discord-trigger';

const messages = defineMessages({
    discord: { id: 'nft.pdp.creator.discord', defaultMessage: 'Discord' },
});

export const DiscordTooltip: FC<{
    children?: string | null;
    onOpen?: () => void;
    linkClass?: string;
}> = ({ children, onOpen, linkClass }) => {
    const { formatMessage } = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [mouseDown, setMouseDown] = useState(false);
    const handleOpen = useCallback(() => {
        onOpen?.();
        setIsOpen(true);
    }, [onOpen]);
    const handleClose = useCallback(() => setIsOpen(false), []);
    const handleBlur = useCallback((e: FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsOpen(false);
    }, []);
    function handleTooltipEsc(e: KeyboardEvent): void {
        if (e.key === 'Escape') {
            setIsOpen(false);
        }
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onBlur={handleBlur}
            onMouseDown={() => setMouseDown(true)}
            onMouseUp={() => setMouseDown(false)}
            onMouseLeave={() => setMouseDown(false)}
            className={styles.socialTooltip}
        >
            <Link
                className={classnames(styles.socialLink, linkClass)}
                ariaDescribedBy={TOOLTIP_ID}
                id={TRIGGER_ID}
                onClick={e => {
                    e.preventDefault();
                    handleOpen();
                }}
                onFocus={handleOpen}
                onKeyDown={handleTooltipEsc}
                href={'user:' + children}
            >
                <DiscordLogo
                    className={styles.socialIcon}
                    ariaLabel={formatMessage(messages.discord)}
                    role="img"
                />
            </Link>
            <Tooltip
                id={TOOLTIP_ID}
                direction="top"
                isVisible={isOpen}
                keepInViewport
                paddingSize="xSmall"
                align="center"
                closeOnOutsideClick={!mouseDown}
                onClose={handleClose}
            >
                <input
                    className={styles.discordInput}
                    aria-labelledby={TRIGGER_ID}
                    readOnly
                    value={children || ''}
                />
            </Tooltip>
        </div>
    );
};
